import {
  INVALID_SMS_CODE_ERRORS,
  INVALID_TOTP_CODE_ERRORS,
  MFA_REQUIRED_ERROR,
  RECENT_LOGIN_REQUIRED_ERROR,
  SMS_TOO_MANY_REQUESTS_ERROR,
  UNVERIFIED_EMAIL_ERROR,
  confirmTotpEnrollment,
  createFreshRecaptcha,
  getMfaResolver,
  getResolverFactors,
  reauthenticateWithGoogle,
  reauthenticateWithPassword,
  resolveSmsSignIn,
  resolveTotpSignIn,
  startSmsChallenge,
  startTotpEnrollment,
  unenrollSms,
  unenrollTotp,
  type TotpEnrollmentStart,
} from "@/features/authentication";
import { trackSettings } from "@/pages/settings/utils/track-settings";
import Button from "@components/ui/button";
import Dialog from "@components/ui/dialog";
import Input from "@components/ui/input";
import { OtpDigits } from "@components/ui/otp-digits";
import { auth } from "@lib/firebase";
import {
  IconAlertCircle,
  IconArrowLeft,
  IconEye,
  IconEyeOff,
  IconLock,
  IconLockCheck,
  IconPasswordFingerprint,
  IconX,
} from "@tabler/icons-react";
import type { MultiFactorResolver, RecaptchaVerifier } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ReactQrCode from "react-qr-code";

// react-qr-code ships CJS-only: Vite's dev-server interop hands the default
// import the whole module object, while vitest and the production build hand
// it the component. Unwrap once so all three agree.
const QRCode =
  (ReactQrCode as unknown as { default?: typeof ReactQrCode }).default ??
  ReactQrCode;

type DialogStep =
  | "intro"
  | "reauth"
  | "reauth-mfa"
  | "qr"
  | "totp"
  | "success";

export type MfaDialogIntent = "setup" | "remove" | "remove-sms";

// What to resume once the reauth step succeeds.
type PendingAction = "totp-enroll" | "remove-totp" | "remove-sms";

const GENERIC_ERROR = "Something went wrong. Please try again.";

// Groups the base32 secret into 4-char chunks, as in the design.
const formatSecretKey = (secretKey: string) =>
  secretKey.replace(/(.{4})/g, "$1 ").trim();

/**
 * The 2FA setup flow as a modal, per the Organization-settings designs:
 * authenticator setup -> success. Also hosts the re-auth step that enrollment
 * and removal need (Firebase requires a recent login for both).
 *
 * SMS enrollment is intentionally unavailable. Because the authenticator app is
 * the only offered method, setup replaces the method-picker step with a single
 * "set up authenticator" intro screen and then runs TOTP enrollment. SMS
 * challenge and removal stay supported so users with a legacy SMS factor can
 * still authenticate and migrate to TOTP.
 *
 * Email verification is NOT here — it happens in the card before this dialog
 * opens. If enrollment still reports an unverified email (stale token), we
 * bail out via onNeedsEmailVerification so the card can handle it.
 */
export const MfaSetupDialog = ({
  open,
  intent,
  onOpenChange,
  onEnrollmentChanged,
  onNeedsEmailVerification,
}: {
  open: boolean;
  intent: MfaDialogIntent;
  onOpenChange: (open: boolean) => void;
  /** Called after any enroll/unenroll so the card can re-read factor state. */
  onEnrollmentChanged: () => void;
  onNeedsEmailVerification: () => void;
}) => {
  const isPasswordUser =
    auth.currentUser?.providerData[0]?.providerId === "password";

  const [step, setStep] = useState<DialogStep>("intro");
  const [enrollment, setEnrollment] = useState<TotpEnrollmentStart | null>(
    null,
  );
  const [totpCode, setTotpCode] = useState("");
  const [password, setPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [pendingAction, setPendingAction] =
    useState<PendingAction>("totp-enroll");
  // Re-auth for an MFA-enrolled user triggers the MFA challenge itself:
  // reauthenticateWithCredential throws auth/multi-factor-auth-required and
  // must be completed with a factor assertion before the pending action runs.
  const [reauthMfaResolver, setReauthMfaResolver] =
    useState<MultiFactorResolver | null>(null);
  const [reauthMfaCode, setReauthMfaCode] = useState("");
  const [reauthSmsVerificationId, setReauthSmsVerificationId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recaptchaContainerRef = useRef<HTMLDivElement>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  // Reset to a clean flow every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    const isRemoval = intent === "remove" || intent === "remove-sms";
    // Authenticator is the only offered method, so instead of a picker the
    // setup flow opens on a single "set up authenticator" intro screen that
    // then kicks off TOTP enrollment.
    setStep(isRemoval ? "reauth" : "intro");
    setPendingAction(
      intent === "remove"
        ? "remove-totp"
        : intent === "remove-sms"
          ? "remove-sms"
          : "totp-enroll",
    );
    setEnrollment(null);
    setTotpCode("");
    setPassword("");
    setIsPasswordVisible(false);
    setReauthMfaResolver(null);
    setReauthMfaCode("");
    setReauthSmsVerificationId("");
    setError(null);
    setIsLoading(false);
  }, [open, intent]);

  // The reCAPTCHA widget can't survive the dialog unmounting its container.
  useEffect(() => {
    if (open) return;
    try {
      recaptchaVerifierRef.current?.clear();
    } catch {
      // Already torn down with the container.
    }
    recaptchaVerifierRef.current = null;
  }, [open]);

  const close = () => onOpenChange(false);

  // Firebase resets the verifier after every phone attempt, so keep the same
  // mounted widget across recent-login recovery. Replacing its DOM node while
  // reCAPTCHA still has queued callbacks crashes the runtime and invalidates
  // the next token.
  const getRecaptchaVerifier = (): RecaptchaVerifier => {
    if (recaptchaVerifierRef.current) {
      return recaptchaVerifierRef.current;
    }
    if (!recaptchaContainerRef.current) {
      throw new Error("reCAPTCHA container is not mounted");
    }
    recaptchaVerifierRef.current = createFreshRecaptcha(
      recaptchaContainerRef.current,
      recaptchaVerifierRef.current,
      "normal",
    );
    return recaptchaVerifierRef.current;
  };

  const handleEnrollmentError = (err: unknown, resume: PendingAction) => {
    const errorCode = (err as { code?: string }).code;
    if (errorCode === RECENT_LOGIN_REQUIRED_ERROR) {
      setPendingAction(resume);
      setStep("reauth");
    } else if (errorCode === UNVERIFIED_EMAIL_ERROR) {
      onNeedsEmailVerification();
    } else {
      console.error("MFA enrollment error:", err);
      setError(GENERIC_ERROR);
    }
  };

  const beginTotpEnrollment = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const started = await startTotpEnrollment();
      setEnrollment(started);
      setStep("qr");
    } catch (err) {
      handleEnrollmentError(err, "totp-enroll");
    } finally {
      setIsLoading(false);
    }
  };

  // Runs the action that the recent-login requirement interrupted, once
  // re-authentication (password/Google, plus the MFA challenge when the user
  // is already enrolled) has fully completed.
  const continueAfterReauth = async () => {
    if (pendingAction === "remove-totp" || pendingAction === "remove-sms") {
      try {
        setIsLoading(true);
        if (pendingAction === "remove-totp") {
          await unenrollTotp();
          trackSettings("removed 2fa security app");
        } else {
          await unenrollSms();
          trackSettings("removed 2fa sms");
        }
        onEnrollmentChanged();
        close();
      } catch (err) {
        console.error("MFA unenroll error:", err);
        setError(GENERIC_ERROR);
      } finally {
        setIsLoading(false);
      }
    } else {
      await beginTotpEnrollment();
    }
  };

  const sendReauthChallengeSms = async (resolver: MultiFactorResolver) => {
    try {
      setError(null);
      const verificationId = await startSmsChallenge(
        resolver,
        getRecaptchaVerifier(),
      );
      setReauthSmsVerificationId(verificationId);
      setReauthMfaCode("");
    } catch (err) {
      console.error("Reauth SMS challenge send error:", err);
      setError(
        (err as { code?: string }).code === SMS_TOO_MANY_REQUESTS_ERROR
          ? "Too many attempts. Please wait a while before trying again."
          : "We couldn't send the text message. Please try again.",
      );
    }
  };

  const handleReauth = async () => {
    try {
      setIsLoading(true);
      setError(null);
      if (isPasswordUser) await reauthenticateWithPassword(password);
      else await reauthenticateWithGoogle();
      setPassword("");
    } catch (err) {
      const errorCode = (err as { code?: string }).code;
      if (errorCode === MFA_REQUIRED_ERROR) {
        // The user is MFA-enrolled, so re-auth itself requires the second
        // factor. Complete it on the reauth-mfa step, then continue.
        const resolver = getMfaResolver(err);
        setReauthMfaResolver(resolver);
        setReauthMfaCode("");
        setReauthSmsVerificationId("");
        setPassword("");
        setStep("reauth-mfa");
        setIsLoading(false);
        // No authenticator on the account -> challenge over SMS.
        if (!getResolverFactors(resolver).hasTotp) {
          await sendReauthChallengeSms(resolver);
        }
        return;
      }
      console.error("Re-authentication error:", err);
      setError(
        isPasswordUser
          ? "Incorrect password. Please try again."
          : GENERIC_ERROR,
      );
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
    await continueAfterReauth();
  };

  const handleConfirmReauthMfa = async () => {
    if (!reauthMfaResolver) return;
    const { hasTotp } = getResolverFactors(reauthMfaResolver);
    try {
      setIsLoading(true);
      setError(null);
      if (hasTotp) {
        await resolveTotpSignIn(reauthMfaResolver, reauthMfaCode);
      } else {
        await resolveSmsSignIn(
          reauthMfaResolver,
          reauthSmsVerificationId,
          reauthMfaCode,
        );
      }
      setReauthMfaResolver(null);
      setReauthMfaCode("");
    } catch (err) {
      const errorCode = (err as { code?: string }).code;
      if (
        errorCode &&
        [...INVALID_TOTP_CODE_ERRORS, ...INVALID_SMS_CODE_ERRORS].includes(
          errorCode,
        )
      ) {
        setError("That code didn't work. Please try again.");
        setReauthMfaCode("");
      } else {
        console.error("Reauth MFA challenge error:", err);
        setError(GENERIC_ERROR);
      }
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
    await continueAfterReauth();
  };

  const handleConfirmTotp = async () => {
    if (!enrollment) return;
    try {
      setIsLoading(true);
      setError(null);
      await confirmTotpEnrollment(enrollment.secret, totpCode);
      onEnrollmentChanged();
      trackSettings("enabled 2fa");
      setStep("success");
    } catch (err) {
      const errorCode = (err as { code?: string }).code;
      if (errorCode && INVALID_TOTP_CODE_ERRORS.includes(errorCode)) {
        setError(
          "That code didn't work. Check your authenticator app and try again.",
        );
        setTotpCode("");
      } else {
        console.error("MFA enrollment confirmation error:", err);
        setError(GENERIC_ERROR);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const BACK_TARGET: Partial<Record<DialogStep, DialogStep>> = {
    qr: "intro",
    totp: "qr",
    reauth: "intro",
    "reauth-mfa": "reauth",
  };

  const goBack = () => {
    setError(null);
    const target = BACK_TARGET[step];
    // Removal reauth starts without the intro screen, so close instead.
    const enteredDirectly = step === "reauth" && intent !== "setup";
    if (!target || enteredDirectly) close();
    else setStep(target);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} closeOnOutsideClick>
      {/* Portaled to <body> so it escapes Dialog.Content's scale transform,
          which would otherwise trap position:fixed inside the dialog. This
          keeps the close button pinned to the screen's top-right corner, on
          the backdrop, per design. z-index sits just above the overlay. */}
      {open &&
        createPortal(
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="tw-fixed tw-right-6 tw-top-6 tw-z-[10001] tw-flex tw-size-9 tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-shadow-sm tw-outline tw-outline-1 tw-outline-blue/5 hover:tw-bg-blue-50"
          >
            <IconX size={18} className="tw-text-blue" />
          </button>,
          document.body,
        )}
      <Dialog.Content
        className="tw-w-[800px] tw-max-w-[92vw]"
        overlayClassName="tw-items-start tw-overflow-y-auto tw-py-12"
      >
        <div className="tw-flex tw-w-full tw-flex-col tw-rounded-2xl tw-bg-white tw-p-6 tw-shadow-sm tw-outline tw-outline-1 tw-outline-blue/5">
          {/* Legacy SMS re-authentication renders reCAPTCHA here. */}
          <div ref={recaptchaContainerRef} />

          {step !== "intro" && step !== "success" && (
            <div className="tw-mb-6 tw-flex tw-w-full tw-items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={goBack}
                className="tw-text-blue"
              >
                <IconArrowLeft className="tw-mr-1" />
                Go back
              </Button>
            </div>
          )}

          {step === "intro" ? (
            <div className="tw-flex tw-flex-col tw-gap-10 tw-pt-2">
              <div className="tw-flex tw-flex-col tw-gap-6">
                <span className="tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-xl tw-bg-violet-100">
                  <IconPasswordFingerprint className="tw-size-6 tw-text-violet-700" />
                </span>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <p className="tw-text-2xl tw-font-semibold tw-leading-8 tw-text-blue">
                    Set up your authenticator app
                  </p>
                  <p className="tw-text-sm tw-leading-5 tw-text-blue-800">
                    Use an authenticator app like Google Authenticator, Authy, or
                    Apple Passwords to generate a one-time code each time you sign
                    in.{" "}
                    <a
                      href="https://safety.google/intl/en-GB_ca/stories/passwort"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tw-text-violet-700 hover:tw-text-violet-800"
                    >
                      Learn more about 2FA
                    </a>
                    .
                  </p>
                </div>
              </div>
              <div>
                <Button
                  size="default"
                  onClick={beginTotpEnrollment}
                  isLoading={isLoading}
                >
                  Get started
                </Button>
              </div>
            </div>
          ) : step === "reauth" ? (
            <div className="tw-flex tw-flex-col tw-gap-8">
              <div className="tw-flex tw-flex-col tw-gap-2">
                <p className="tw-text-2xl tw-font-semibold tw-leading-8 tw-text-blue">
                  Confirm it's you
                </p>
                <p className="tw-text-sm tw-leading-5 tw-text-blue-800">
                  For your security, please confirm your identity before
                  changing two-factor authentication.
                </p>
              </div>
              {isPasswordUser ? (
                <div className="tw-flex tw-flex-col tw-gap-4">
                  <div className="tw-relative tw-w-full tw-max-w-[320px]">
                    <IconLock
                      size={18}
                      className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw-z-10 -tw-translate-y-1/2 tw-text-blue-500"
                    />
                    <Input
                      type={isPasswordVisible ? "text" : "password"}
                      placeholder="Your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && password) handleReauth();
                      }}
                      containerClassName="tw-w-full"
                      className="tw-w-full tw-pl-10 tw-pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setIsPasswordVisible((v) => !v)}
                      className="tw-absolute tw-right-3 tw-top-1/2 tw-flex -tw-translate-y-1/2 tw-cursor-pointer tw-items-center tw-justify-center"
                      aria-label={
                        isPasswordVisible ? "Hide password" : "Show password"
                      }
                    >
                      {isPasswordVisible ? (
                        <IconEye
                          size={18}
                          className="tw-text-blue-500 tw-transition-colors hover:tw-text-blue-600"
                        />
                      ) : (
                        <IconEyeOff
                          size={18}
                          className="tw-text-blue-500 tw-transition-colors hover:tw-text-blue-600"
                        />
                      )}
                    </button>
                  </div>
                  <div>
                    <Button
                      size="default"
                      onClick={handleReauth}
                      isLoading={isLoading}
                      disabled={!password}
                    >
                      Confirm
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <Button
                    variant="secondary"
                    size="default"
                    onClick={handleReauth}
                    isLoading={isLoading}
                  >
                    Continue with Google
                  </Button>
                </div>
              )}
            </div>
          ) : step === "reauth-mfa" ? (
            <div className="tw-flex tw-flex-col tw-gap-8">
              <div className="tw-flex tw-flex-col tw-gap-2">
                <p className="tw-text-2xl tw-font-semibold tw-leading-8 tw-text-blue">
                  Confirm it's you
                </p>
                <p className="tw-text-sm tw-leading-5 tw-text-blue-800">
                  {reauthMfaResolver &&
                  getResolverFactors(reauthMfaResolver).hasTotp
                    ? "Enter the 6-digit code from your authenticator app to continue."
                    : `We've sent a code by text message to the number ending in ${
                        reauthMfaResolver
                          ? getResolverFactors(
                              reauthMfaResolver,
                            ).smsPhoneHint.slice(-2)
                          : ""
                      }. Enter it below to continue.`}
                </p>
              </div>
              <OtpDigits
                value={reauthMfaCode}
                onChange={setReauthMfaCode}
                disabled={isLoading}
              />
              {reauthMfaResolver &&
                !getResolverFactors(reauthMfaResolver).hasTotp && (
                  <p className="tw-text-sm tw-leading-5 tw-text-blue-800">
                    Didn't receive it?{" "}
                    <button
                      type="button"
                      onClick={() =>
                        reauthMfaResolver &&
                        sendReauthChallengeSms(reauthMfaResolver)
                      }
                      className="tw-text-violet-700 hover:tw-text-violet-800"
                    >
                      Send a new code
                    </button>
                  </p>
                )}
              <div>
                <Button
                  size="default"
                  onClick={handleConfirmReauthMfa}
                  isLoading={isLoading}
                  disabled={reauthMfaCode.length !== 6}
                >
                  Confirm
                </Button>
              </div>
            </div>
          ) : step === "qr" && enrollment ? (
            <div className="tw-flex tw-items-center tw-gap-6">
              <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-8">
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <p className="tw-text-2xl tw-font-semibold tw-leading-8 tw-text-blue">
                    Scan the QR code
                  </p>
                  <p className="tw-text-sm tw-leading-5 tw-text-blue-800">
                    Open your authenticator app and scan this code to connect it
                    to your Storydoc account. Don't have an app? Download one
                    from your app store.
                  </p>
                </div>
                <div className="tw-flex tw-flex-col tw-gap-2 tw-text-sm tw-leading-5">
                  <p className="tw-font-semibold tw-text-blue">
                    Can't scan it?
                  </p>
                  <p className="tw-text-blue-800">
                    Enter this setup key manually:
                    <br />
                    <span className="tw-select-all tw-break-all">
                      {formatSecretKey(enrollment.secretKey)}
                    </span>
                  </p>
                </div>
                <div>
                  <Button size="default" onClick={() => setStep("totp")}>
                    Next step
                  </Button>
                </div>
              </div>
              <div className="tw-shrink-0 tw-rounded-2xl tw-bg-white tw-p-4 tw-shadow-xs tw-outline tw-outline-1 tw-outline-blue/5">
                <QRCode value={enrollment.qrCodeUrl} size={224} />
              </div>
            </div>
          ) : step === "totp" ? (
            <div className="tw-flex tw-flex-col tw-gap-8">
              <div className="tw-flex tw-flex-col tw-gap-2">
                <p className="tw-text-2xl tw-font-semibold tw-leading-8 tw-text-blue">
                  Enter code
                </p>
                <p className="tw-text-sm tw-leading-5 tw-text-blue-800">
                  Enter the 6-digit code from your authenticator app to confirm
                  the connection.
                </p>
              </div>
              <OtpDigits
                value={totpCode}
                onChange={setTotpCode}
                disabled={isLoading}
              />
              <div>
                <Button
                  size="default"
                  onClick={handleConfirmTotp}
                  isLoading={isLoading}
                  disabled={totpCode.length !== 6}
                >
                  Next step
                </Button>
              </div>
            </div>
          ) : step === "success" ? (
            <div className="tw-flex tw-flex-col tw-gap-12 tw-pt-2">
              <div className="tw-flex tw-flex-col tw-gap-6">
                <span className="tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-xl tw-bg-violet-100">
                  <IconLockCheck className="tw-size-6 tw-text-violet-700" />
                </span>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <p className="tw-text-2xl tw-font-semibold tw-leading-8 tw-text-blue">
                    Two-factor authentication is enabled
                  </p>
                  <p className="tw-text-sm tw-leading-5 tw-text-blue-800">
                    Your account is now protected with an additional layer of
                    security.
                    <br />
                    You can manage your settings anytime in{" "}
                    <span className="tw-font-semibold">
                      My settings › Security & privacy › Two-factor
                      authentication (2FA).
                    </span>
                  </p>
                </div>
              </div>
              <div>
                <Button size="default" onClick={close}>
                  Done
                </Button>
              </div>
            </div>
          ) : null}

          {error && (
            <div className="tw-mt-4 tw-flex tw-items-center tw-text-xs tw-text-red-500">
              <IconAlertCircle
                size={16}
                className="tw-mr-1.5 tw-shrink-0 tw-text-red-500"
              />
              {error}
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
